import React, { useState } from 'react';
import styles from './break-create.module.scss';
import {
  ModalWindow,
  DefaultButton,
  MiniInput,
  TimeRangePicker,
  showConfirmDialog,
} from 'shared/ui';
import { BookingApi } from 'shared/api';
import { businessStore } from 'entities/index';
import * as dateFns from 'date-fns';
import { ICalendarBreakType } from 'shared/api/booking/interfaces/calendar-break-type.interface';
import { DATE_FORMAT } from 'shared/consts/date-format';
import { parseTimeFromDatetime } from 'shared/lib';

interface ITimeSlot {
  startedAt: string;
  finishedAt: string;
}

interface IBreakCreateProps {
  startTime?: string;
  employeeId: string;
  date: Date;
  onClose: () => void;
  onCreated: () => void;
  isEdit?: boolean;
  breakData?: ICalendarBreakType;
  existingBookings?: ITimeSlot[];
  existingBreaks?: ITimeSlot[];
}

export const BreakCreate: React.FC<IBreakCreateProps> = ({
  startTime,
  employeeId,
  date,
  onClose,
  onCreated,
  isEdit = false,
  breakData,
  existingBookings = [],
  existingBreaks = [],
}) => {
  const [timeRange, setTimeRange] = useState({
    start:
      isEdit && breakData?.timeRule.startedAt
        ? parseTimeFromDatetime(breakData.timeRule.startedAt)
        : startTime || '12:00',
    end:
      isEdit && breakData?.timeRule.finishedAt
        ? parseTimeFromDatetime(breakData.timeRule.finishedAt)
        : startTime
          ? `${String(Number(startTime.split(':')[0]) + (Number(startTime.split(':')[1]) >= 30 ? 1 : 0)).padStart(2, '0')}:${Number(startTime.split(':')[1]) >= 30 ? String(Number(startTime.split(':')[1]) - 30).padStart(2, '0') : String(Number(startTime.split(':')[1]) + 30).padStart(2, '0')}`
          : '12:30',
  });
  const [breakTitle, setBreakTitle] = useState(
    isEdit && breakData?.title ? breakData.title : 'Перерыв'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTimeRangeChange = (data: {
    dayIndex: number;
    timeRange: { start: string | null; end: string | null };
  }) => {
    const newStart = data.timeRange.start || '12:00';
    const newEnd = data.timeRange.end || '13:00';
    setTimeRange({ start: newStart, end: newEnd });
    if (newStart >= newEnd) {
      const [h, m] = newStart.split(':').map(Number);
      const endH = h + 1;
      setTimeRange({
        start: newStart,
        end: `${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
      });
    }
  };

  const timeToMinutes = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const handleCreate = async () => {
    const pointId = businessStore.pointId;
    if (!pointId) {
      setError('Point ID not found');
      return;
    }

    const newStart = timeToMinutes(timeRange.start);
    const newEnd = timeToMinutes(timeRange.end);

    const overlappingItems: string[] = [];

    if (existingBookings && !isEdit) {
      for (const b of existingBookings) {
        const bStart = timeToMinutes(b.startedAt);
        const bEnd = timeToMinutes(b.finishedAt);
        if (newStart < bEnd && newEnd > bStart) {
          overlappingItems.push(
            `Запись клиента ${b.startedAt}–${b.finishedAt}`
          );
        }
      }
    }

    if (existingBreaks && !isEdit) {
      for (const b of existingBreaks) {
        const bStart = timeToMinutes(b.startedAt);
        const bEnd = timeToMinutes(b.finishedAt);
        if (newStart < bEnd && newEnd > bStart) {
          overlappingItems.push(`Перерыв ${b.startedAt}–${b.finishedAt}`);
        }
      }
    }

    if (overlappingItems.length > 0) {
      const confirmed = await showConfirmDialog(
        `Новый перерыв (${timeRange.start}–${timeRange.end}) пересекается с: ${overlappingItems.join(', ')}. Всё равно создать?`
      );
      if (!confirmed) return;
    }

    setIsLoading(true);
    setError(null);

    let success = false;

    try {
      if (isEdit && breakData) {
        await BookingApi.updateBreakOverride(breakData.id, {
          title: breakTitle,
          startedAt: timeRange.start,
          finishedAt: timeRange.end,
          date: dateFns.format(date, DATE_FORMAT),
        });
      } else {
        await BookingApi.breakOverride({
          title: breakTitle,
          userId: employeeId,
          pointId,
          date: dateFns.format(date, DATE_FORMAT),
          startedAt: timeRange.start,
          finishedAt: timeRange.end,
        });
      }
      success = true;
      onCreated();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : isEdit
            ? 'Ошибка при сохранении перерыва'
            : 'Ошибка при создании перерыва'
      );
    } finally {
      if (!success) {
        setIsLoading(false);
      }
    }
  };

  return (
    <ModalWindow onClickOutside={onClose}>
      <div className={styles.modalContent}>
        <div className={styles.title}>
          {isEdit ? 'Редактирование перерыва' : 'Создание перерыва'}
        </div>
        <div className={styles.field}>
          <label className={styles.label}>Время</label>

          <div className={styles.content__time_selector}>
            <TimeRangePicker
              index={0}
              timeRange={timeRange}
              timeInterval={5}
              onChange={handleTimeRangeChange}
            />
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Название</label>
          <MiniInput
            inputValue={breakTitle}
            onChange={setBreakTitle}
            inputPlaceholder="Перерыв"
            inputType="text"
            style={{ width: '100%', padding: '8px 12px' }}
          />
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actionButtons}>
          <DefaultButton
            className={styles.button_action}
            buttonText="Отменить"
            onClick={onClose}
            color="red"
          />
          <DefaultButton
            className={styles.button_action}
            buttonText={
              isLoading
                ? isEdit
                  ? 'Сохранение...'
                  : 'Создание...'
                : isEdit
                  ? 'Сохранить'
                  : 'Создать'
            }
            onClick={handleCreate}
            disabled={isLoading || timeRange.end <= timeRange.start}
            color="green"
          />
        </div>
      </div>
    </ModalWindow>
  );
};
